import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Package, Cpu, Zap, Shield, Wrench } from 'lucide-react';

const services = [
  {
    id: 'rental-fitouts',
    name: 'Rental Fit-Outs',
    description: 'Complete warehouse fit-out solutions on flexible rental terms. Transform empty shells into operational facilities without heavy capital investment.',
    icon: Package,
    items: ['Racking Systems', 'Shelving Systems', 'Mezzanine Structures', 'Office Cabins', 'Docking Equipment', 'Flooring Solutions'],
    color: '#E63946',
  },
  {
    id: 'material-handling',
    name: 'Material Handling & Automation',
    description: 'Integrated material handling equipment and automation solutions to streamline operations and boost productivity.',
    icon: Cpu,
    items: ['Conveyor Systems', 'Shuttle Systems', 'AGVs/AMRs', 'Pick-to-Light Systems', 'Sortation Systems', 'Packaging Equipment'],
    color: '#E63946',
  },
  {
    id: 'energy-efficiency',
    name: 'Energy Efficiency Upgrades',
    description: 'Sustainable energy solutions for warehouse operations. Reduce operational costs while minimizing environmental impact.',
    icon: Zap,
    items: ['LED Lighting Systems', 'Solar-Ready Integration', 'Smart Energy Sensors', 'HVAC Optimization', 'Insulation Upgrades', 'Energy Monitoring'],
    color: '#10B981',
  },
  {
    id: 'security-iot',
    name: 'Security & IoT Monitoring',
    description: 'Advanced security and real-time monitoring solutions for complete visibility and protection of your assets.',
    icon: Shield,
    items: ['CCTV Systems', 'Access Control', 'IoT Sensors', 'Real-time Dashboards', 'Alarm Systems', 'Fire Detection'],
    color: '#EF4444',
  },
  {
    id: 'maintenance',
    name: 'Preventive Maintenance & Support',
    description: 'Comprehensive maintenance programs for optimal uptime. Keep your infrastructure running at peak performance.',
    icon: Wrench,
    items: ['Scheduled Inspections', 'Preventive Maintenance', 'Emergency Repairs', 'Lifecycle Support', 'Spare Parts Management', 'Technical Training'],
    color: '#8B5CF6',
  },
];

const process = [
  { step: '01', title: 'Consultation', description: 'Understand your requirements and assess your facility' },
  { step: '02', title: 'Design', description: 'Custom solution design tailored to your needs' },
  { step: '03', title: 'Proposal', description: 'Detailed proposal with flexible rental terms' },
  { step: '04', title: 'Installation', description: 'Professional installation with minimal disruption' },
  { step: '05', title: 'Support', description: 'Ongoing maintenance and technical support' },
];

export default function Services() {
  return (
    <div className="bg-[#0a1628] pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tighter mb-6">
              Complete Warehouse <span className="text-[#E63946]">Infrastructure Solutions</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-3xl">
              From fit-outs to automation, energy efficiency to security — we provide comprehensive services to transform your warehouse into a modern, efficient facility.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-[#0f1e32]" data-testid="services-grid">
        <div className="container-custom">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}
                data-testid={`service-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="w-14 h-14 flex items-center justify-center"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <service.icon className="w-7 h-7" style={{ color: service.color }} />
                    </div>
                    <span className="font-mono text-sm" style={{ color: service.color }}>
                      0{index + 1}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-4">
                    {service.name}
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <Link to="/get-quote" data-testid={`service-cta-${service.id}`}>
                    <Button className="btn-secondary h-11 px-6 text-sm">
                      Learn More <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div 
                    className="industrial-card bg-[#0a1628] border p-8"
                    style={{ borderColor: `${service.color}30` }}
                  >
                    <h3 className="font-heading text-lg font-bold text-white uppercase mb-6">
                      What's Included:
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {service.items.map((item, iIndex) => (
                        <div
                          key={iIndex}
                          className="flex items-center gap-3 p-3 bg-[#0f1e32] border border-white/5"
                        >
                          <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: service.color }} />
                          <span className="text-gray-300 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Our Process
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
              How We Work
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A streamlined process designed to deliver results quickly and efficiently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {process.map((step, index) => (
              <div
                key={index}
                className="relative group"
                data-testid={`process-step-${index}`}
              >
                <div className="industrial-card bg-[#0f1e32] border border-white/10 p-6 h-full hover:border-[#E63946]/50 transition-colors">
                  <div className="font-mono text-3xl font-bold text-[#E63946] mb-4">{step.step}</div>
                  <h3 className="font-heading text-lg font-bold text-white uppercase mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0f1e32]">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0a1628] border border-[#E63946]/30 p-12 md:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
                  Need a Complete <span className="text-[#E63946]">Infrastructure Partner?</span>
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed">
                  We offer bundled solutions that combine multiple services for maximum efficiency and cost savings. Let us design a comprehensive package for your facility.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link to="/get-quote" data-testid="services-get-quote">
                  <Button className="btn-gold clip-slant h-14 px-10 text-base w-full sm:w-auto">
                    Get a Quote <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary h-14 px-10 text-base w-full sm:w-auto">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
